import {
  Dictionary,
  Literal,
  Optional,
  Array as RArray,
  Boolean as RBoolean,
  Record as RRecord,
  String as RString,
  Static,
  Union,
} from "runtypes";

import { FilledDynamicValueTableColumnTypeLiteral } from "../DynamicValue.js";
import { DisplayTableColumnId } from "../idTypeBrands.js";
import { ScopeItemType } from "../scopeTypes.js";
import { DataType } from "../semantic-layer/generated/hex_sl_schema.js";
import {
  typedObjectEntries,
  typedObjectFromEntries,
} from "../utils/typedObjects.js";

export const CalcColumnDefinition = RRecord({
  name: DisplayTableColumnId,
  expression: RString,
  // `muted` is no longer supported, it's just around for backwards compatibility

  muted: Optional(RBoolean.nullable()),
});
export type CalcColumnDefinition = Static<typeof CalcColumnDefinition>;

export const CalcReferencedColumnTypes = Dictionary(
  FilledDynamicValueTableColumnTypeLiteral,
  DisplayTableColumnId,
);
export type CalcReferencedColumnTypes = Static<
  typeof CalcReferencedColumnTypes
>;

export const CalcReferencedColumn = RRecord({
  columnId: DisplayTableColumnId,
  columnType: FilledDynamicValueTableColumnTypeLiteral,
  containsAggregate: RBoolean,
});
export type CalcReferencedColumn = Static<typeof CalcReferencedColumn>;

/** The set of ScopeItemTypes that calcs expressions can handle */
export const CalcScopeItemTypeSubset = Union(
  Literal("STRING"),
  Literal("NUMBER"),
  Literal("BOOLEAN"),
  Literal("DATE"),
  Literal("DATETIME"),
);
export type CalcScopeItemTypeSubset = Static<typeof CalcScopeItemTypeSubset>;
// make sure `CalcScopeItemTypeSubset` is actually a subset of `ScopeItemType`
null! as CalcScopeItemTypeSubset satisfies ScopeItemType;

export const CalcReferencedParamTypes = Dictionary(
  CalcScopeItemTypeSubset,
  RString,
);
export type CalcReferencedParamTypes = Static<typeof CalcReferencedParamTypes>;

export const CalcsDefinition = RRecord({
  calcColumns: RArray(CalcColumnDefinition),
  referencedColumns: Optional(RArray(CalcReferencedColumn)),
  referencedParamTypes: Optional(CalcReferencedParamTypes.nullable()),
  // @deprecated - use `referencedColumns` after applying `normalizeCalcsDefinition`
  referencedColumnTypes: CalcReferencedColumnTypes,
});
export type CalcsDefinition = Static<typeof CalcsDefinition>;

export const EMPTY_CALCS: CalcsDefinition = {
  calcColumns: [],
  referencedColumns: [],
  referencedColumnTypes: {},
};

/**
 * Normalize the provided calcs definition in place, ensuring all fields
 * are present and up to date.
 */
export function normalizeCalcsDefinition(
  source: CalcsDefinition,
  inPlace = false,
): Required<CalcsDefinition> {
  const result: Required<CalcsDefinition> = inPlace
    ? (source as Required<CalcsDefinition>)
    : {
        referencedParamTypes: source.referencedParamTypes ?? {},
        referencedColumns: [],
        ...source,
      };

  // ensure `referencedColumnTypes` is up to date with the source of truth
  // `referencedColumns` field.
  if (source.referencedColumns) {
    result.referencedColumnTypes = typedObjectFromEntries(
      source.referencedColumns.map((column) => [
        column.columnId,
        column.columnType,
      ]),
    );
  } else {
    // in this case, the calc definition was created before `.referencedColumns`
    // was introduced, so we need to migrate it in.
    result.referencedColumns = typedObjectEntries(
      source.referencedColumnTypes,
    ).map(([columnId, columnType]) => ({
      columnId,
      columnType,
      // before `.referencedColumns` was introduced, all column references
      // were non-aggregates
      containsAggregate: false,
    }));
  }
  return result;
}

export const CALC_TYPE_TO_HQL_TYPE: {
  [K in CalcScopeItemTypeSubset]: DataType;
} = {
  STRING: "string",
  NUMBER: "number",
  BOOLEAN: "boolean",
  DATE: "date",
  DATETIME: "timestamptz",
} as const;
